import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { MARKET_SUMMARY_QUERY } from "../../../legacy/queries/MI";
import {
  MarketSummaryVariables,
  MarketSummaryPayload,
} from "../../../legacy/types/companySearch/MI";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { COMPANY_SEARCH_CLIENT } from "../../../utils/consts";
import { toCustomSpendCategory } from "../../../utils/workspace/customSpendCategory";
import type { TamSpendType } from "../../../types/workspace";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
} from "../../../generated/company-search";

interface Props {
  companyFilters?: CompanyFilter;
  spendCategoryWeights?: { id: string; weight: number }[];
  spendType?: TamSpendType | null;
  localizeInstalls?: boolean;
  skip?: boolean;
  /** V2 ("model tier") scoring narrowing — mirrors opportunityGeneratorSearch's
   * args. Only honoured together (modelScores resolves the model the filter
   * targets); omit all three when no single model is active. */
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  /** Share-page panel tag (Apollo context); see toPanelDescriptor. */
  activeModelId?: string;
  /** Apollo's `loading` is true only for the very first fetch by default —
   * a later variable change (e.g. the company filters changing) silently
   * keeps showing the outdated figures until the new ones arrive. Callers
   * that re-render with new variables often (Market Sizing's tier cards)
   * should opt in so `loading` covers those refetches too, not just the
   * first load. Defaults to false to keep existing consumers' behavior. */
  notifyOnVariableChange?: boolean;
}

const filterNullValues = (
  companyFilters?: CompanyFilter
): CompanyFilter | undefined => {
  if (!companyFilters) return undefined;

  const filtered = { ...companyFilters };

  if (filtered.employees) {
    filtered.employees = {
      min: filtered.employees.min === null ? undefined : filtered.employees.min,
      max: filtered.employees.max === null ? undefined : filtered.employees.max,
    };
  }

  if (filtered.revenue) {
    filtered.revenue = {
      min: filtered.revenue.min === null ? undefined : filtered.revenue.min,
      max: filtered.revenue.max === null ? undefined : filtered.revenue.max,
    };
  }

  return filtered;
};

export const useMarketSummary = ({
  companyFilters,
  spendCategoryWeights,
  spendType,
  localizeInstalls = true,
  skip = false,
  tenantId,
  modelScores,
  modelScoreFilter,
  activeModelId,
  notifyOnVariableChange = false,
}: Props) => {
  const companyMatchFile = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const {
    data: marketSpendData,
    loading,
    error,
  } = useQuery<MarketSummaryPayload, MarketSummaryVariables>(
    MARKET_SUMMARY_QUERY,
    {
      variables: {
        companyFilters: filterNullValues(companyFilters),
        companyMatchFile:
          companyFilters?.opportunityType &&
          companyMatchFile?.fileId &&
          companyMatchFile?.lastMatched
            ? companyMatchFile
            : undefined,
        // An empty categories list is not a valid request: hermes rejects an
        // empty spend_category_ids array outright, and an empty custom
        // category crashes its weights-CTE builder. Send the key only when
        // there is at least one category to sum.
        customSpendCategory: toCustomSpendCategory(
          spendCategoryWeights,
          spendType
        ),
        localizeInstalls,
        tenantId,
        modelScores,
        modelScoreFilter,
      },
      // totalMarketSummary is a *spend* aggregate and hermes requires at least
      // one spend category for it, so this only answers "how much addressable
      // spend". The always-present company-count baseline comes from
      // opportunity_generator_search instead (see MarketSizingContent).
      skip: skip || !spendCategoryWeights?.length,
      context: { clientName: COMPANY_SEARCH_CLIENT, activeModelId },
      notifyOnNetworkStatusChange: notifyOnVariableChange,
    }
  );

  const { companyCount, totalSpend } = useMemo(() => {
    return {
      companyCount: marketSpendData?.totalMarketSummary?.companyCount || 0,
      totalSpend: marketSpendData?.totalMarketSummary?.totalSpend || 0,
    };
  }, [marketSpendData]);

  return {
    loading,
    error,
    companyCount,
    totalSpend,
  };
};
